import React from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { FileText, Loader2, Archive, Download, ExternalLink } from 'lucide-react';

function fmtEUR(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Number(v).toLocaleString('fr-FR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + ' €';
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
}

function Field({ label, value, mono, strong }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} ${strong ? 'font-bold text-slate-900' : 'text-slate-800'}`}>
        {value}
      </div>
    </div>
  );
}

export default function PropositionPreviewDialog({ previewDevis, setPreviewDevis, setPreviewBlob, previewLoading, data, handleDownload }) {
  return (
      <Dialog open={!!previewDevis} onOpenChange={(o) => { if (!o) { setPreviewDevis(null); setPreviewBlob(null); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Proposition ESS — {previewDevis}
            </DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              {(() => {
                const it = data.items.find(i => i.devis_id === previewDevis);
                if (!it) return null;
                return (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <Field label="Devis" value={it.devis_id} mono />
                    <Field label="Version" value={`v${it.version}`} />
                    <Field label="Membre" value={it.membre_raison_sociale || '—'} />
                    <Field label="Email" value={it.membre_email || '—'} />
                    <Field label="Montant HT" value={fmtEUR(it.montant_ht)} />
                    <Field label="Montant Global" value={fmtEUR(it.montant_ttc)} strong />
                    <Field label="CA déclaré" value={it.ca_ht ? fmtEUR(it.ca_ht) : '—'} />
                    <Field label="Type" value={it.type_souscription || '—'} />
                    <Field label="Émis le" value={fmtDate(it.created_at)} />
                    <Field label={it.statut === 'signe' ? 'Signé le' : 'Échéance'} value={fmtDate(it.statut === 'signe' ? it.signed_at : it.expires_at)} />
                    {it.signer_full_name && <Field label="Signataire" value={it.signer_full_name} strong />}
                    {it.ged_document_id && (
                      <div className="col-span-2 mt-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                        <Archive className="h-4 w-4 text-emerald-700" />
                        <div className="text-xs">
                          <div className="font-semibold text-emerald-900">Archivé en GED</div>
                          <div className="text-emerald-700 font-mono">{it.ged_document_id}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => handleDownload(previewDevis)} data-testid="registre-preview-download">
                  <Download className="h-4 w-4 mr-2" /> Télécharger
                </Button>
                <Button variant="outline" onClick={() => {
                  window.open(`/admin/super-dashboard?tab=ged-ess&devis=${encodeURIComponent(previewDevis)}`, '_self');
                }}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir dans la GED
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
}
